import { useState, useRef, useCallback } from 'react'
import { Link, router } from '@inertiajs/react'
import { AdminLayout } from '@/layouts/AdminLayout'
import Markdown from '@/components/Markdown'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Save, Send, X, ImageIcon, Eye, Code, Upload, Loader2 } from 'lucide-react'

interface Tag {
  id: number
  name: string
  slug: string
}

interface PostData {
  id?: number
  title: string
  slug: string
  body: string
  excerpt: string
  author_name: string
  cover_image_url?: string | null
  meta_title: string
  meta_description: string
  tag_ids: number[]
  status?: string
}

interface Props {
  post: PostData | null
  tags: Tag[]
  errors?: string[]
}

export default function Form({ post, tags, errors = [] }: Props) {
  const isEditing = !!post?.id
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    body: post?.body || '',
    excerpt: post?.excerpt || '',
    author_name: post?.author_name || '',
    meta_title: post?.meta_title || '',
    meta_description: post?.meta_description || '',
    tag_ids: post?.tag_ids || [],
  })
  const [showPreview, setShowPreview] = useState(false)
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
  const [uploading, setUploading] = useState(false)
  const bodyRef = useRef<HTMLTextAreaElement>(null)
  const inlineImageRef = useRef<HTMLInputElement>(null)
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverPreview, setCoverPreview] = useState<string | null>(post?.cover_image_url || null)
  const [removeCover, setRemoveCover] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newTagName, setNewTagName] = useState('')

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    if (field === 'title' && !isEditing && !slugManuallyEdited) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      setFormData((prev) => ({ ...prev, slug }))
    }
  }

  const handleTagToggle = (tagId: number) => {
    setFormData((prev) => {
      const current = prev.tag_ids
      if (current.includes(tagId)) {
        return { ...prev, tag_ids: current.filter((id) => id !== tagId) }
      } else {
        return { ...prev, tag_ids: [...current, tagId] }
      }
    })
  }

  const handleInlineImageUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)

    try {
      const data = new FormData()
      data.append('file', file)

      const response = await fetch('/admin/uploads/image', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: data,
      })

      if (response.ok) {
        const { markdown } = await response.json()
        const textarea = bodyRef.current
        const cursorPos = textarea?.selectionStart ?? formData.body.length

        setFormData((prev) => {
          const body = prev.body
          return { ...prev, body: body.slice(0, cursorPos) + '\n' + markdown + '\n' + body.slice(cursorPos) }
        })
      }
    } catch (err) {
      // ignore
    } finally {
      setUploading(false)
      if (inlineImageRef.current) inlineImageRef.current.value = ''
    }
  }, [formData.body])

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setCoverImage(file)
    setRemoveCover(false)
    setCoverPreview(URL.createObjectURL(file))
  }

  const handleRemoveCover = () => {
    setCoverImage(null)
    setCoverPreview(null)
    setRemoveCover(true)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return
    try {
      const response = await fetch('/admin/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ name: newTagName.trim() }),
      })
      if (response.ok) {
        const tag = await response.json()
        tags.push(tag)
        setFormData((prev) => ({ ...prev, tag_ids: [...prev.tag_ids, tag.id] }))
        setNewTagName('')
      }
    } catch (e) {
      // ignore
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    const data = new FormData()
    data.append('post[title]', formData.title)
    data.append('post[slug]', formData.slug)
    data.append('post[body]', formData.body)
    data.append('post[excerpt]', formData.excerpt)
    data.append('post[author_name]', formData.author_name)
    data.append('post[meta_title]', formData.meta_title)
    data.append('post[meta_description]', formData.meta_description)
    formData.tag_ids.forEach((id) => data.append('tag_ids[]', id.toString()))

    if (coverImage) {
      data.append('cover_image', coverImage)
    }
    if (removeCover) {
      data.append('remove_cover_image', 'true')
    }

    if (isEditing) {
      data.append('_method', 'PATCH')
      router.post(`/admin/posts/${post!.id}`, data, {
        forceFormData: true,
        onFinish: () => setIsSubmitting(false),
      })
    } else {
      router.post('/admin/posts', data, {
        forceFormData: true,
        onFinish: () => setIsSubmitting(false),
      })
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/admin/posts">
              <ArrowLeft className="mr-1 h-4 w-4" /> Volver
            </Link>
          </Button>
          <h1 className="text-2xl font-bold text-foreground">
            {isEditing ? 'Editar Post' : 'Nuevo Post'}
          </h1>
        </div>

        {errors.length > 0 && (
          <div className="rounded-lg bg-destructive/15 p-4">
            <ul className="list-disc list-inside text-sm text-destructive">
              {errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Contenido</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Título *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="Título del post"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="slug">URL Slug</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">/blog/</span>
                      <Input
                        id="slug"
                        value={formData.slug}
                        onChange={(e) => { setSlugManuallyEdited(true); handleChange('slug', e.target.value) }}
                        placeholder="url-del-post"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="excerpt">Extracto</Label>
                    <Textarea
                      id="excerpt"
                      value={formData.excerpt}
                      onChange={(e) => handleChange('excerpt', e.target.value)}
                      placeholder="Breve descripción para listados y SEO..."
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="body">Contenido (Markdown)</Label>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={uploading}
                          onClick={() => inlineImageRef.current?.click()}
                        >
                          {uploading ? (
                            <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Subiendo...</>
                          ) : (
                            <><Upload className="mr-1 h-4 w-4" /> Imagen</>
                          )}
                        </Button>
                        <input
                          ref={inlineImageRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleInlineImageUpload}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPreview(!showPreview)}
                        >
                          {showPreview ? (
                            <><Code className="mr-1 h-4 w-4" /> Editar</>
                          ) : (
                            <><Eye className="mr-1 h-4 w-4" /> Preview</>
                          )}
                        </Button>
                      </div>
                    </div>
                    {showPreview ? (
                      <div className="min-h-[400px] rounded-md border border-input p-4">
                        {formData.body ? (
                          <Markdown>{formData.body}</Markdown>
                        ) : (
                          <p className="text-muted-foreground italic">Nada que previsualizar</p>
                        )}
                      </div>
                    ) : (
                      <Textarea
                        ref={bodyRef}
                        id="body"
                        value={formData.body}
                        onChange={(e) => handleChange('body', e.target.value)}
                        placeholder="Escribe el contenido en Markdown..."
                        rows={20}
                        className="font-mono text-sm"
                      />
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">SEO</CardTitle>
                  <CardDescription>Optimización para buscadores</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="meta_title">Meta Título</Label>
                    <Input
                      id="meta_title"
                      value={formData.meta_title}
                      onChange={(e) => handleChange('meta_title', e.target.value)}
                      placeholder="Título para Google (máx 60 caracteres)"
                      maxLength={60}
                    />
                    <p className="text-xs text-muted-foreground">{formData.meta_title.length}/60</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="meta_description">Meta Descripción</Label>
                    <Textarea
                      id="meta_description"
                      value={formData.meta_description}
                      onChange={(e) => handleChange('meta_description', e.target.value)}
                      placeholder="Descripción para Google (máx 160 caracteres)"
                      rows={2}
                      maxLength={160}
                    />
                    <p className="text-xs text-muted-foreground">{formData.meta_description.length}/160</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Publicación</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditing && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Estado:</span>
                      <Badge variant={post?.status === 'published' ? 'success' : 'secondary'}>
                        {post?.status === 'published' ? 'Publicado' : 'Borrador'}
                      </Badge>
                    </div>
                  )}
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    <Save className="mr-2 h-4 w-4" />
                    {isSubmitting ? 'Guardando...' : 'Guardar'}
                  </Button>
                  {isEditing && post?.status === 'draft' && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => router.post(`/admin/posts/${post!.id}/publish`)}
                    >
                      <Send className="mr-2 h-4 w-4" /> Publicar ahora
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Cover image */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Imagen de Portada</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {coverPreview ? (
                    <div className="relative">
                      <img src={coverPreview} alt="Cover" className="w-full h-32 object-cover rounded" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2 h-6 w-6"
                        onClick={handleRemoveCover}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      className="w-full h-32 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:border-primary transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCoverSelect}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {coverPreview ? 'Cambiar imagen' : 'Subir imagen'}
                  </Button>
                </CardContent>
              </Card>

              {/* Tags */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Etiquetas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => {
                      const isSelected = formData.tag_ids.includes(tag.id)
                      return (
                        <Badge
                          key={tag.id}
                          variant={isSelected ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => handleTagToggle(tag.id)}
                        >
                          {tag.name}
                          {isSelected && <X className="ml-1 h-3 w-3" />}
                        </Badge>
                      )
                    })}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Nueva etiqueta"
                      className="flex-1"
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag() } }}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={handleCreateTag}>
                      +
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Author */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Autor</CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    value={formData.author_name}
                    onChange={(e) => handleChange('author_name', e.target.value)}
                    placeholder="Nombre del autor"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  )
}
